// store/devices.js
import axios from 'axios';
import router from '../router';

function apiBase() {
  return process.env.VUE_APP_API_URL || 'http://127.0.0.1:3000/api';
}

function authHeaders() {
  const token = localStorage.getItem('userToken');
  if (!token) {
    router.push({ name: 'login' });
    return null;
  }
  return { Authorization: `Bearer ${token}` };
}

const state = {
  devices: [],
};

const mutations = {
  SET_DEVICES(state, devices) {
    state.devices = devices;
  },
  REMOVE_DEVICE(state, deviceUuid) {
    state.devices = state.devices.filter(d => d.device_uuid !== deviceUuid);
  },
  // Same idea as REMOVE_DEVICE but for multiple uuids in a single
  // commit — keeps the v-data-table from re-rendering N times in a row.
  REMOVE_DEVICES(state, deviceUuids) {
    const toRemove = new Set(deviceUuids);
    state.devices = state.devices.filter(d => !toRemove.has(d.device_uuid));
  },
  UPDATE_CAN_EDIT(state, { deviceUuids, canEdit }) {
    state.devices = state.devices.map(d =>
      deviceUuids.includes(d.device_uuid) ? { ...d, can_edit: canEdit } : d
    );
  },
  // Mirror of UPDATE_CAN_EDIT for the delete flag. Both flags live on
  // the same row but they're controlled independently — admin might
  // give a tablet edit rights but not delete rights, for instance.
  UPDATE_CAN_DELETE(state, { deviceUuids, canDelete }) {
    state.devices = state.devices.map(d =>
      deviceUuids.includes(d.device_uuid) ? { ...d, can_delete: canDelete } : d
    );
  },
  // Combined mutation used by the pairing-cascade paths in
  // updateCanEdit / updateCanDelete, AND by updateAllowBackdating
  // (single-key change, no cascade). Lets us set one or more flags in a
  // single commit so v-data-table re-renders once, not twice.
  UPDATE_PERMISSIONS(state, { deviceUuids, changes }) {
    const ids = new Set(deviceUuids);
    state.devices = state.devices.map(d =>
      ids.has(d.device_uuid) ? { ...d, ...changes } : d
    );
  },
};

const actions = {
  async fetchDevices({ commit }) {
    const headers = authHeaders();
    if (!headers) return;
    try {
      const response = await axios.get(`${apiBase()}/devices`, { headers });
      commit('SET_DEVICES', response.data);
    } catch (error) {
      console.error('Error fetching devices:', error);
      throw error;
    }
  },

  async deleteDevice({ commit }, deviceUuid) {
    const headers = authHeaders();
    if (!headers) return;
    try {
      await axios.delete(`${apiBase()}/devices/${deviceUuid}`, { headers });
      commit('REMOVE_DEVICE', deviceUuid);
    } catch (error) {
      console.error('Error deleting device:', error);
      throw error;
    }
  },

  // Bulk delete. The backend route (/devices/bulk-delete) runs the
  // whole batch in a single transaction, so either every device is
  // gone or none of them are — we mirror that here by only mutating
  // the store after the request succeeds. The response includes how
  // many freetext labels were released so the caller can surface that
  // information if relevant.
  async bulkDeleteDevices({ commit }, deviceUuids) {
    const headers = authHeaders();
    if (!headers) return;
    try {
      const { data } = await axios.post(
        `${apiBase()}/devices/bulk-delete`,
        { deviceUuids },
        { headers }
      );
      commit('REMOVE_DEVICES', deviceUuids);
      return data;
    } catch (error) {
      console.error('Error bulk-deleting devices:', error);
      throw error;
    }
  },

  async assignToLocation({ dispatch }, { deviceUuid, locationId }) {
    const headers = authHeaders();
    if (!headers) return;
    try {
      await axios.put(
        `${apiBase()}/devices/${deviceUuid}/location`,
        { locationId },
        { headers }
      );
      await dispatch('fetchDevices');
    } catch (error) {
      console.error('Error assigning device to location:', error);
      throw error;
    }
  },

  // Bulk-toggle can_edit on the selected devices.
  //
  // PAIRING INVARIANT (2026-06-14): can_delete=1 requires can_edit=1.
  // Backend enforces this in the same UPDATE — when canEdit=false is
  // requested it ALSO drops can_delete to 0. The response includes
  // cascadedCanDelete: true when that happened, and we mirror the
  // cascade in the optimistic store commit so UI doesn't flicker
  // between the partial and the full update.
  //
  // Returns the backend response so the caller can show a snackbar
  // that mentions the cascade ("Redigering deaktiveret — sletning
  // blev også deaktiveret"). DevicesView.vue still calls fetchDevices
  // afterwards as a belt-and-braces refresh.
  async updateCanEdit({ commit }, { deviceUuids, canEdit }) {
    const headers = authHeaders();
    if (!headers) return;
    try {
      const { data } = await axios.put(
        `${apiBase()}/devices/can-edit`,
        { deviceUuids, canEdit },
        { headers }
      );

      // Optimistic update mirrors what the backend actually did. If
      // the backend reports it cascaded can_delete to 0, commit both
      // flags in a single mutation rather than dropping just can_edit
      // and leaving can_delete chips stale until the refetch lands.
      const changes = { can_edit: canEdit ? 1 : 0 };
      if (data && data.cascadedCanDelete) {
        changes.can_delete = 0;
      }
      commit('UPDATE_PERMISSIONS', { deviceUuids, changes });

      return data;
    } catch (error) {
      console.error('Error updating can_edit:', error);
      throw error;
    }
  },

  // Mirror of updateCanEdit. Promoting can_delete=true also promotes
  // can_edit on the backend (the delete button only surfaces in edit
  // mode, so the two are paired). Same response shape:
  //   { success, affectedRows, cascadedCanEdit }
  async updateCanDelete({ commit }, { deviceUuids, canDelete }) {
    const headers = authHeaders();
    if (!headers) return;
    try {
      const { data } = await axios.put(
        `${apiBase()}/devices/can-delete`,
        { deviceUuids, canDelete },
        { headers }
      );

      const changes = { can_delete: canDelete ? 1 : 0 };
      if (data && data.cascadedCanEdit) {
        changes.can_edit = 1;
      }
      commit('UPDATE_PERMISSIONS', { deviceUuids, changes });

      return data;
    } catch (error) {
      console.error('Error updating can_delete:', error);
      throw error;
    }
  },

  // Bulk-set productDisplayMode on the selected devices.
  //
  // Not a permission and NOT paired with anything. The original request
  // was "when images are on, editing should be off" — but locking
  // editing is a permission and can_edit already is one. Tying them
  // means an experienced cook loses her edit rights the moment she
  // picks pictures because they're faster, and someone who must not
  // edit keeps theirs by picking names. The admin can set both in one
  // pass; the data stays independent.
  //
  // Values: 'text' | 'image' | 'user'. The backend rejects anything
  // else, because the column is a VARCHAR and a typo would reach the
  // tablet, which coerces the unknown back to 'text' — the setting
  // would appear to do nothing with no error anywhere.
  async updateDisplayMode({ commit }, { deviceUuids, productDisplayMode }) {
    const headers = authHeaders();
    if (!headers) return;
    try {
      const { data } = await axios.put(
        `${apiBase()}/devices/display-mode`,
        { deviceUuids, productDisplayMode },
        { headers }
      );

      commit('UPDATE_PERMISSIONS', {
        deviceUuids,
        changes: { productDisplayMode },
      });

      return data;
    } catch (error) {
      console.error('Error updating productDisplayMode:', error);
      throw error;
    }
  },

  // Bulk-toggle allowBackdating on the selected devices.
  //
  // NO CASCADE. Unlike can_edit/can_delete, backdating is independent —
  // it only governs the print-date anchor, nothing to do with editing.
  // So we just set the one column and mirror it in the store. The
  // backend response is { success, affectedRows } (no cascade flags).
  // DevicesView.vue still calls fetchDevices afterwards as a refresh.
  async updateAllowBackdating({ commit }, { deviceUuids, allowBackdating }) {
    const headers = authHeaders();
    if (!headers) return;
    try {
      const { data } = await axios.put(
        `${apiBase()}/devices/allow-backdating`,
        { deviceUuids, allowBackdating },
        { headers }
      );

      commit('UPDATE_PERMISSIONS', {
        deviceUuids,
        changes: { allowBackdating: allowBackdating ? 1 : 0 },
      });

      return data;
    } catch (error) {
      console.error('Error updating allowBackdating:', error);
      throw error;
    }
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
};